import {useIntl} from 'react-intl';
import {batch, useDispatch, useSelector} from 'react-redux';

import FastModal from 'components/FastModal';
import {getAppViewMode} from 'stores/selectors/appStateSelectors';
import {TipCaddyType, TrayType, ViewMode} from 'constants/dataEnum';
import {hideTypedModal} from 'stores/actions/typedModalAction';
import {getModalData} from 'stores/selectors/typedModalSelectors';
import {abortProtocol, postManualReteachReturnTips, resumeProtocol} from 'stores/business/instrumentBusiness';
import {toTitleCase} from 'lib/stringHelper';
import FormattedHTMLMessage from 'components/FormattedHTMLMessage';
import {selectSessionUserUsername} from 'stores/selectors/sessionSelectors';
import {clearModalError} from 'stores/actions/modalStateAction';
import style from './ModalCaddyDetection.module.scss';

const TrayText = {
  [TrayType.HIGH_VOLUME_CADDY]: 'High Volume Tray',
  [TrayType.NORMAL_CADDY]: 'Low Volume Tray',
  [TrayType.DOUBLE_CADDY]: 'Dual Caddy Tray',
};

function ModalCaddyDetection() {
  const dispatch = useDispatch();
  const {formatMessage} = useIntl();
  const viewMode = useSelector(getAppViewMode);
  const modalData = useSelector(getModalData);
  const username = useSelector(selectSessionUserUsername);
  const {trayType, firstNestLocation, secondNestLocation, expectedCaddy} = modalData;
  const expectedCaddyTitleCase = toTitleCase(expectedCaddy);

  const content = (
    <div className={style.container}>
      <p className={style.description}>
        {formatMessage(
          {
            id:
              viewMode === ViewMode.PLATE_TYPE_EDITOR
                ? 'modal.caddyDetection.content.reteachPlate'
                : 'modal.caddyDetection.content.runtime',
          },
          {
            nests: (
              <FormattedHTMLMessage
                id={
                  trayType === TrayType.DOUBLE_CADDY && secondNestLocation
                    ? 'modal.caddyDetection.content.doubleTray'
                    : 'modal.caddyDetection.content.singleTray'
                }
                values={{firstNestLocation, secondNestLocation}}
              />
            ),
          },
        )}
      </p>
      <div className={style.contentContainer}>
        <div className={style.imageContainer}>
          <img
            className={style.image}
            src={`./images/tip-caddies/${expectedCaddy === TipCaddyType.HIGH_VOLUME_CADDY ? 'hv-tip-caddy' : 'lv-tip-caddy'}.png`}
            alt="tip caddy"
          />
          <span>{expectedCaddyTitleCase}</span>
        </div>
        <div>
          <p>
            <span> {formatMessage({id: 'modal.caddyDetection.content.currentTray'})}</span>{' '}
            <span className={style[expectedCaddy]}>{TrayText[trayType]}</span>
          </p>
          <p>
            <span> {formatMessage({id: 'modal.caddyDetection.content.expectedCaddy'})}</span>{' '}
            <span className={style[expectedCaddy]}>{expectedCaddyTitleCase}</span>
          </p>
        </div>
      </div>
    </div>
  );

  const handleCloseClick = () => {
    dispatch(postManualReteachReturnTips(username, false));
    dispatch(hideTypedModal());
    dispatch(clearModalError());
  };

  const handleRescanClick = () => {
    batch(() => {
      dispatch(resumeProtocol(false, false, true));
      dispatch(hideTypedModal());
    });
  };

  const handleIgnoreClick = () => {
    batch(() => {
      dispatch(resumeProtocol(true, false, true));
      dispatch(hideTypedModal());
    });
  };

  const handleAbortClick = () => {
    batch(() => {
      dispatch(abortProtocol(true));
      dispatch(hideTypedModal());
    });
  };

  const getPlateTypeEditorButtons = () => [
    {
      className: 'btn-lg',
      style: 'primary',
      text: formatMessage({id: 'modal.caddyDetection.button.close'}),
      handlerClick: handleCloseClick,
    },
  ];

  const getRuntimeButtons = () => [
    {
      className: 'btn-lg',
      style: 'primary',
      text: formatMessage({id: 'modal.caddyDetection.button.rescan'}),
      handlerClick: handleRescanClick,
    },
    {
      className: 'btn-lg',
      style: 'secondary',
      type: 'danger',
      text: formatMessage({id: 'modal.caddyDetection.button.ignore'}),
      handlerClick: handleIgnoreClick,
    },
    {
      className: 'btn-lg',
      style: 'primary',
      type: 'danger',
      text: formatMessage({id: 'modal.caddyDetection.button.abort'}),
      handlerClick: handleAbortClick,
    },
  ];

  const buttons = viewMode === ViewMode.PLATE_TYPE_EDITOR ? getPlateTypeEditorButtons() : getRuntimeButtons();

  return (
    <FastModal
      show
      header={formatMessage({id: 'modal.caddyDetection.title'})}
      content={content}
      btnAction={buttons}
      size="lg"
    />
  );
}

export default ModalCaddyDetection;
